/**
 * useChat – Zustand store for the ARIA conversational interface.
 * Maintains the message thread and handles API calls.
 */
import { create } from 'zustand'
import { postChat } from '@/lib/api'
import type { ChatMessage } from '@/lib/types'

interface ChatState {
  messages: ChatMessage[]
  sending: boolean
  error: string | null
  send: (text: string) => Promise<void>
  clear: () => void
}

// Neutral seed — no hardcoded portfolio figures (real-data-only rule: the
// backend injects live portfolio context per conversation).
const SEED: ChatMessage = {
  id: 'seed_0',
  role: 'aria',
  content:
    "Hello — I'm ARIA, your Adaptive Reasoning & Intelligence Agent. " +
    "I have live context on your portfolio. " +
    "Ask me about **risk**, **goals**, **rebalancing**, **market conditions**, " +
    "or your **behavioural profile**.",
  timestamp: new Date(),
}

export const useChat = create<ChatState>()((set, get) => ({
  messages: [SEED],
  sending:  false,
  error:    null,

  send: async (text: string) => {
    const userMsg: ChatMessage = {
      id:        `user_${Date.now()}`,
      role:      'user',
      content:   text,
      timestamp: new Date(),
    }

    set(s => ({ messages: [...s.messages, userMsg], sending: true, error: null }))

    try {
      const res = await postChat(text, get().messages)
      const ariaMsg: ChatMessage = {
        id:        `aria_${Date.now()}`,
        role:      'aria',
        content:   res.message,
        timestamp: new Date(res.timestamp),
      }
      set(s => ({ messages: [...s.messages, ariaMsg], sending: false }))
    } catch (e) {
      set({ sending: false, error: 'ARIA is temporarily unavailable. Please try again.' })
    }
  },

  clear: () => set({ messages: [SEED], error: null }),
}))
